import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';
import { useCurrency } from '../../../../../context/CurrencyContext';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

const inputClass =
  'h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-sm text-slate-900 shadow-sm outline-none ring-brand-500/30 focus:border-brand-500 focus:ring-2';
const primaryBtnClass =
  'inline-flex h-9 min-w-[5.5rem] shrink-0 items-center justify-center rounded-lg bg-slate-900 px-4 text-sm font-medium text-white shadow-sm hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60';

function ProfitMiniTile(props: {
  label: string;
  value: string;
  valueClassName: string;
  subline?: React.ReactNode;
  hint?: string;
  variant?: 'default' | 'amber' | 'brand';
}) {
  const { label, value, valueClassName, subline, hint, variant = 'default' } = props;
  const shell =
    variant === 'amber'
      ? 'border-amber-200/80 bg-amber-50/40'
      : variant === 'brand'
        ? 'border-brand-300/70 bg-gradient-to-br from-brand-50/50 to-white ring-1 ring-brand-500/10'
        : 'border-slate-200/90 bg-white';
  return (
    <div
      className={`min-h-[5.25rem] rounded-xl border px-3 py-2.5 shadow-sm ${shell}`}
      title={hint}
    >
      <p className="text-[10px] font-bold uppercase leading-tight tracking-wide text-slate-500 sm:text-xs">
        {label}
      </p>
      <p
        className={[
          'mt-1 break-words text-lg font-bold tabular-nums leading-tight sm:text-xl md:text-2xl',
          valueClassName,
        ].join(' ')}
      >
        {value}
      </p>
      {subline ? (
        <div className="mt-1 text-[10px] leading-snug text-slate-500 sm:text-xs">{subline}</div>
      ) : null}
    </div>
  );
}

type ProfitRow = {
  currency: string;
  ordersCount: number;
  /** Sum of order subtotals before discounts (merchandise only). */
  merchandiseSubtotalMinor: number;
  /** Sum of order-level discounts / coupon savings (reduces merchandise revenue). */
  discountMinor: number;
  referralDiscountMinor?: number;
  retentionDiscountMinor?: number;
  promotionCodeDiscountMinor?: number;
  checkoutLineDiscountMinor?: number;
  ordersWithDiscountCount: number;
  ordersWithPromotionCount: number;
  /** Total charged (products + delivery − discounts); avoid comparing directly to COGS */
  revenueMinor?: number;
  merchandiseRevenueMinor: number;
  deliveryFeesChargedMinor: number;
  totalCollectedMinor: number;
  recognizedRevenueExcludingLiabilityMinor?: number;
  preorderDepositLiabilityMinor?: number;
  openPreorderCount?: number;
  cogsMinor: number;
  cogsNetMinor?: number;
  cogsReliefMinor?: number;
  deliveryActualCostMinor: number;
  refundMinor?: number;
  refundCount?: number;
  merchandiseRefundedMinor?: number;
  merchandiseNetRevenueMinor?: number;
  refundDeliveryMinor?: number;
  reshipSupplementCogsMinor?: number;
  merchandiseGrossProfitMinor: number;
  deliveryMarginMinor: number;
  profitMinor: number;
};

export default function ProfitReportsPage() {
  const { t } = useTranslation();
  const { authorizedFetch } = useAuth();
  const { formatMajorInCurrency } = useCurrency();
  const [workspaceScope, setWorkspaceScope] = useState<string>(() => {
    if (typeof window === 'undefined') return '__all__';
    return window.localStorage.getItem('workspace_department_scope') || '__all__';
  });
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<ProfitRow[]>([]);
  const isAllScope = useMemo(() => {
    const scope = String(workspaceScope || '').trim().toLowerCase();
    return !scope || scope === '__all__';
  }, [workspaceScope]);
  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      if (from) qs.set('from', from);
      if (to) qs.set('to', to);
      const res = await authorizedFetch(`${API_BASE}/finance/profit-report?${qs.toString()}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as any)?.message || 'Failed');
      }
      const data = (await res.json()) as { rows?: ProfitRow[] };
      setRows(data.rows ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const readScope = () =>
      (typeof window !== 'undefined'
        ? window.localStorage.getItem('workspace_department_scope')
        : null) || '__all__';
    const handleScopeChange = () => {
      setWorkspaceScope(readScope());
    };
    window.addEventListener(
      'workspace-department-scope:change',
      handleScopeChange as EventListener,
    );
    window.addEventListener('storage', handleScopeChange);
    return () => {
      window.removeEventListener(
        'workspace-department-scope:change',
        handleScopeChange as EventListener,
      );
      window.removeEventListener('storage', handleScopeChange);
    };
  }, []);

  useEffect(() => {
    void load();
    // Keep profit report in sync when workspace scope changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceScope]);

  const totals = rows.reduce(
    (acc, r) => {
      acc.subtotal += r.merchandiseSubtotalMinor ?? 0;
      acc.discount += r.discountMinor ?? 0;
      acc.discountOrders += r.ordersWithDiscountCount ?? 0;
      acc.promoOrders += r.ordersWithPromotionCount ?? 0;
      acc.merchGrossPreRefund += r.merchandiseRevenueMinor;
      acc.merchNet += r.merchandiseNetRevenueMinor ?? r.merchandiseRevenueMinor;
      acc.merchRefunded += r.merchandiseRefundedMinor ?? 0;
      acc.delFees += r.deliveryFeesChargedMinor;
      acc.collected += r.totalCollectedMinor;
      acc.recognizedRevenue += r.recognizedRevenueExcludingLiabilityMinor ?? r.totalCollectedMinor;
      acc.preorderLiability += r.preorderDepositLiabilityMinor ?? 0;
      acc.openPreorders += r.openPreorderCount ?? 0;
      acc.cogsRaw += r.cogsMinor;
      acc.cogsNet += r.cogsNetMinor ?? r.cogsMinor;
      acc.cogsRelief += r.cogsReliefMinor ?? 0;
      acc.reshipCogs += r.reshipSupplementCogsMinor ?? 0;
      acc.deliveryActual += r.deliveryActualCostMinor;
      acc.refunds += r.refundMinor ?? 0;
      acc.refundDelivery += r.refundDeliveryMinor ?? 0;
      acc.refundCount += r.refundCount ?? 0;
      acc.merchGross += r.merchandiseGrossProfitMinor;
      acc.delMargin += r.deliveryMarginMinor;
      acc.profit += r.profitMinor;
      acc.orders += r.ordersCount;
      return acc;
    },
    {
      subtotal: 0,
      discount: 0,
      discountOrders: 0,
      promoOrders: 0,
      merchGrossPreRefund: 0,
      merchNet: 0,
      merchRefunded: 0,
      delFees: 0,
      collected: 0,
      recognizedRevenue: 0,
      preorderLiability: 0,
      openPreorders: 0,
      cogsRaw: 0,
      cogsNet: 0,
      cogsRelief: 0,
      reshipCogs: 0,
      deliveryActual: 0,
      refunds: 0,
      refundDelivery: 0,
      refundCount: 0,
      merchGross: 0,
      delMargin: 0,
      profit: 0,
      orders: 0,
    },
  );

  const usd = (n: number) => formatMajorInCurrency(n / 100, 'USD');

  return (
    <MainLayout withSidebar sidebarItems={getBackofficeSidebarItems(t)} activePath="/workspace/finance/profit">
      <main className="workspace-page">
        <section className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
          <h1 className="text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">
            {t('backoffice.profit_reports_title', 'Profit reports')}
          </h1>
          <p
            className="max-w-3xl text-xs leading-relaxed text-slate-600 sm:text-sm"
            title={t(
              'backoffice.profit_reports_subtitle',
              'Merchandise gross profit uses net product sales after order-level discounts and promotions (subtotal − discount) and FIFO lot COGS. Delivery fees are separate; net profit includes delivery margin. Hover tiles for definitions.',
            )}
          >
            {t(
              'backoffice.profit_reports_subtitle_short',
              'Net merch = subtotal − discounts − merchandise refunds; gross profit uses FIFO COGS net of pro‑rata refund relief (full merch refunds neutralize that sale). Reship replacement COGS is included in FIFO; net profit subtracts delivery‑fee refunds only.',
            )}
          </p>
        </section>

        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">{error}</div>
        ) : null}

        <section className="glass-panel space-y-3 p-4 sm:space-y-4 sm:p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="grid w-full grid-cols-2 gap-3 sm:max-w-lg">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">From</label>
                <input className={inputClass} type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">To</label>
                <input className={inputClass} type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} />
              </div>
            </div>
            <button type="button" className={primaryBtnClass} onClick={load} disabled={loading}>
              {loading ? t('common.loading', 'Loading…') : t('backoffice.profit_run', 'Run report')}
            </button>
          </div>
          {isAllScope ? (
            <p className="text-xs font-medium text-amber-700">
              {t(
                'backoffice.profit_scope_all_hint',
                'Viewing all departments. Select a specific department scope to inspect one department only.',
              )}
            </p>
          ) : null}

          {rows.length === 0 ? (
            <p className="text-sm text-slate-600">{loading ? t('common.loading') : 'No data for this range.'}</p>
          ) : (
            <>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 sm:text-xs">
                {t('backoffice.profit_overview_heading', 'Period overview — hover tiles for definitions')}
              </p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-2.5 2xl:grid-cols-8">
                <ProfitMiniTile
                  label={t('backoffice.profit_subtotal_pre_short', 'Merch. subtotal')}
                  value={usd(totals.subtotal)}
                  valueClassName="text-slate-900"
                  hint={t(
                    'backoffice.profit_subtotal_pre',
                    'Sum of order merchandise subtotals before discounts (excludes delivery).',
                  )}
                />
                <ProfitMiniTile
                  label={t('backoffice.profit_discounts_total', 'Discounts')}
                  value={totals.discount > 0 ? `−${formatMajorInCurrency(totals.discount / 100, 'USD')}` : usd(0)}
                  valueClassName="text-amber-800"
                  variant="amber"
                  subline={t('backoffice.profit_discount_orders_hint', {
                    defaultValue: '{{withDiscount}} disc. · {{withCode}} promo',
                    withDiscount: String(totals.discountOrders),
                    withCode: String(totals.promoOrders),
                  })}
                  hint={t(
                    'backoffice.profit_discount_bridge_intro',
                    'Order-level promotion savings (discountUsd). Reduces merchandise revenue before COGS.',
                  )}
                />
                <ProfitMiniTile
                  label={t('backoffice.profit_net_merch_short', 'Net merch')}
                  value={usd(totals.merchNet)}
                  valueClassName="text-slate-900"
                  subline={
                    totals.merchRefunded > 0
                      ? t('backoffice.profit_merch_after_refund_sub', {
                          defaultValue: 'after −{{merchRef}} merch refunds (pre-refund {{gross}})',
                          merchRef: formatMajorInCurrency(totals.merchRefunded / 100, 'USD'),
                          gross: formatMajorInCurrency(totals.merchGrossPreRefund / 100, 'USD'),
                        })
                      : undefined
                  }
                  hint={t(
                    'backoffice.profit_merch_revenue_hint',
                    'Net product sales after order-level discounts and after merchandise portion of refunds (delivery refunds kept separate).',
                  )}
                />
                <ProfitMiniTile
                  label={t('backoffice.profit_recognized_revenue_short', 'Recognized revenue')}
                  value={usd(totals.recognizedRevenue)}
                  valueClassName="text-slate-900"
                  hint={t(
                    'backoffice.profit_recognized_revenue_hint',
                    'Paid revenue net of open pre-order deposit liabilities.',
                  )}
                />
                <ProfitMiniTile
                  label={t('backoffice.profit_preorder_liability_short', 'Pre-order liability')}
                  value={usd(totals.preorderLiability)}
                  valueClassName="text-amber-800"
                  variant="amber"
                  subline={t('backoffice.profit_open_preorders_sub', {
                    defaultValue: '{{count}} open pre-orders',
                    count: totals.openPreorders,
                  })}
                  hint={t(
                    'backoffice.profit_preorder_liability_hint',
                    'Deposits on open pre-orders treated as liability until final fulfillment.',
                  )}
                />
                <ProfitMiniTile
                  label={t('backoffice.profit_cogs_short', 'COGS')}
                  value={usd(totals.cogsNet)}
                  valueClassName="text-rose-700"
                  subline={
                    totals.cogsRelief > 0 || totals.reshipCogs > 0
                      ? t('backoffice.profit_cogs_subline', {
                          defaultValue: 'FIFO {{raw}} · relief {{rel}} · reship+ {{ship}}',
                          raw: formatMajorInCurrency(totals.cogsRaw / 100, 'USD'),
                          rel: formatMajorInCurrency(totals.cogsRelief / 100, 'USD'),
                          ship: formatMajorInCurrency(totals.reshipCogs / 100, 'USD'),
                        })
                      : undefined
                  }
                  hint={t(
                    'backoffice.profit_cogs_hint',
                    'FIFO lot COGS after pro‑rata relief on merchandise refunds. Total includes replacement (reship) picks with no extra merch revenue.',
                  )}
                />
                <ProfitMiniTile
                  label={t('backoffice.profit_merch_gross_short', 'Merch. gross')}
                  value={usd(totals.merchGross)}
                  valueClassName={totals.merchGross >= 0 ? 'text-emerald-700' : 'text-rose-700'}
                  hint={t(
                    'backoffice.profit_merch_gross_hint',
                    '(Net merch after refunds) − (FIFO COGS after refund relief). Matches economic margin on sold goods.',
                  )}
                />
                <ProfitMiniTile
                  label={t('backoffice.profit_delivery_fees_short', 'Del. fees')}
                  value={usd(totals.delFees)}
                  valueClassName="text-slate-900"
                  hint={t('backoffice.profit_delivery_fees_hint', 'What customers paid for shipping on these orders.')}
                />
                <ProfitMiniTile
                  label={t('backoffice.profit_delivery_actuals_short', 'Del. actual')}
                  value={usd(totals.deliveryActual)}
                  valueClassName="text-amber-800"
                  hint={t(
                    'backoffice.profit_delivery_actuals_hint',
                    'Recorded carrier / fulfillment costs for the period.',
                  )}
                />
                <ProfitMiniTile
                  label={t('backoffice.profit_refunds_short', 'Refunds')}
                  value={totals.refunds > 0 ? `−${usd(totals.refunds)}` : usd(0)}
                  valueClassName="text-rose-700"
                  variant="amber"
                  subline={`${totals.refundCount} records`}
                  hint={t(
                    'backoffice.profit_refunds_hint',
                    'Refunds in period for orders in this report cohort. Merch share unwinds COGS pro‑rata; delivery share hits net profit with delivery margin.',
                  )}
                />
                <ProfitMiniTile
                  label={t('backoffice.profit_net', 'Net profit')}
                  value={usd(totals.profit)}
                  valueClassName={totals.profit >= 0 ? 'text-emerald-700' : 'text-rose-700'}
                  variant="brand"
                  subline={
                    <>
                      {totals.orders === 1
                        ? t('backoffice.profit_orders_paid_one', '1 paid order')
                        : t('backoffice.profit_orders_paid_other', '{{count}} paid orders', { count: totals.orders })}
                      {' · '}
                      {t('backoffice.profit_collected_inline', 'collected {{amount}}', {
                        amount: formatMajorInCurrency(totals.collected / 100, 'USD'),
                      })}
                    </>
                  }
                  hint={t(
                    'backoffice.profit_net_hint',
                    'Merchandise gross profit + delivery margin − delivery-fee refunds. Merchandise refunds are already in net merch and COGS relief.',
                  )}
                />
              </div>
              <div className="overflow-x-auto rounded-xl border border-slate-200/90 bg-white shadow-sm ring-1 ring-slate-900/[0.04]">
                <table className="w-full min-w-[64rem] table-auto text-left text-sm">
                <thead className="bg-slate-100/90 text-xs font-semibold uppercase tracking-wide text-slate-600">
                  <tr>
                    <th className="whitespace-nowrap px-3 py-2.5">Currency</th>
                    <th className="whitespace-nowrap px-3 py-2.5">Orders</th>
                    <th className="whitespace-nowrap px-3 py-2.5">
                      {t('backoffice.profit_col_subtotal', 'Subtotal')}
                    </th>
                    <th className="whitespace-nowrap px-3 py-2.5">
                      {t('backoffice.profit_col_discount', 'Discount')}
                    </th>
                    <th className="whitespace-nowrap px-3 py-2.5">
                      {t('backoffice.profit_col_merch_rev', 'Merch. rev. (net)')}
                    </th>
                    <th className="whitespace-nowrap px-3 py-2.5">
                      {t('backoffice.profit_col_recognized_revenue', 'Recognized rev.')}
                    </th>
                    <th className="whitespace-nowrap px-3 py-2.5">
                      {t('backoffice.profit_col_preorder_liability', 'Pre-order liability')}
                    </th>
                    <th className="whitespace-nowrap px-3 py-2.5">
                      {t('backoffice.profit_col_cogs_net', 'COGS (net)')}
                    </th>
                    <th className="whitespace-nowrap px-3 py-2.5">
                      {t('backoffice.profit_col_merch_gross', 'Merch. gross')}
                    </th>
                    <th className="whitespace-nowrap px-3 py-2.5">
                      {t('backoffice.profit_col_del_fees', 'Del. fees')}
                    </th>
                    <th className="whitespace-nowrap px-3 py-2.5">
                      {t('backoffice.profit_col_del_actual', 'Del. act.')}
                    </th>
                    <th className="whitespace-nowrap px-3 py-2.5">
                      {t('backoffice.profit_col_del_margin', 'Del. margin')}
                    </th>
                    <th className="whitespace-nowrap px-3 py-2.5">
                      {t('backoffice.profit_col_total_paid', 'Paid')}
                    </th>
                    <th className="whitespace-nowrap px-3 py-2.5">
                      {t('backoffice.profit_col_refunds', 'Refunds')}
                    </th>
                    <th className="whitespace-nowrap px-3 py-2.5">
                      {t('backoffice.profit_col_net', 'Net')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-800">
                  {rows.map((r) => (
                    <tr key={r.currency} className="hover:bg-slate-50/80">
                      <td className="whitespace-nowrap px-3 py-2 font-medium">{r.currency}</td>
                      <td className="px-3 py-2">
                        <span className="block tabular-nums">{r.ordersCount}</span>
                        {(r.ordersWithDiscountCount ?? 0) > 0 || (r.ordersWithPromotionCount ?? 0) > 0 ? (
                          <span className="mt-0.5 block text-[10px] leading-tight text-slate-500 sm:text-xs">
                            {t('backoffice.profit_row_discount_meta', {
                              defaultValue: '{{d}} w/ disc. · {{p}} promo',
                              d: String(r.ordersWithDiscountCount ?? 0),
                              p: String(r.ordersWithPromotionCount ?? 0),
                            })}
                          </span>
                        ) : null}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 tabular-nums text-slate-600">
                        {formatMajorInCurrency((r.merchandiseSubtotalMinor ?? 0) / 100, r.currency)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 tabular-nums text-amber-800">
                        {(r.discountMinor ?? 0) > 0 ? (
                          <span className="block">
                            <span>
                              −{formatMajorInCurrency((r.discountMinor ?? 0) / 100, r.currency)}
                            </span>
                            {r.referralDiscountMinor !== undefined ? (
                              <span className="mt-0.5 block max-w-[18rem] text-[10px] font-normal leading-tight text-amber-900/85">
                                {t('backoffice.profit_discount_split', {
                                  defaultValue:
                                    'Ref {{ref}} · Ret {{ret}} · Code {{code}} · Line {{line}}',
                                  ref: formatMajorInCurrency(
                                    (r.referralDiscountMinor ?? 0) / 100,
                                    r.currency,
                                  ),
                                  ret: formatMajorInCurrency(
                                    (r.retentionDiscountMinor ?? 0) / 100,
                                    r.currency,
                                  ),
                                  code: formatMajorInCurrency(
                                    (r.promotionCodeDiscountMinor ?? 0) / 100,
                                    r.currency,
                                  ),
                                  line: formatMajorInCurrency(
                                    (r.checkoutLineDiscountMinor ?? 0) / 100,
                                    r.currency,
                                  ),
                                })}
                              </span>
                            ) : null}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 tabular-nums">
                        {formatMajorInCurrency(
                          (r.merchandiseNetRevenueMinor ?? r.merchandiseRevenueMinor) / 100,
                          r.currency,
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 tabular-nums">
                        {formatMajorInCurrency(
                          (r.recognizedRevenueExcludingLiabilityMinor ?? r.totalCollectedMinor) / 100,
                          r.currency,
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 tabular-nums text-amber-800">
                        {formatMajorInCurrency((r.preorderDepositLiabilityMinor ?? 0) / 100, r.currency)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 tabular-nums">
                        {formatMajorInCurrency((r.cogsNetMinor ?? r.cogsMinor) / 100, r.currency)}
                      </td>
                      <td
                        className={[
                          'whitespace-nowrap px-3 py-2 font-semibold tabular-nums',
                          r.merchandiseGrossProfitMinor >= 0 ? 'text-emerald-800' : 'text-rose-700',
                        ].join(' ')}
                      >
                        {formatMajorInCurrency(r.merchandiseGrossProfitMinor / 100, r.currency)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 tabular-nums">
                        {formatMajorInCurrency(r.deliveryFeesChargedMinor / 100, r.currency)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 tabular-nums">
                        {formatMajorInCurrency(r.deliveryActualCostMinor / 100, r.currency)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 tabular-nums">
                        {formatMajorInCurrency(r.deliveryMarginMinor / 100, r.currency)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 tabular-nums text-slate-600">
                        {formatMajorInCurrency(r.totalCollectedMinor / 100, r.currency)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 tabular-nums text-rose-700">
                        {(r.refundMinor ?? 0) > 0
                          ? `−${formatMajorInCurrency((r.refundMinor ?? 0) / 100, r.currency)}`
                          : '—'}
                      </td>
                      <td
                        className={[
                          'whitespace-nowrap px-3 py-2 text-base font-bold tabular-nums',
                          r.profitMinor >= 0 ? 'text-emerald-800' : 'text-rose-800',
                        ].join(' ')}
                      >
                        {formatMajorInCurrency(r.profitMinor / 100, r.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      </main>
    </MainLayout>
  );
}

